"use client";

import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, ShieldCheck, Users, Car, Plane, Clock } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ROUTE_PRICES, formatPrice, type Currency } from "@/lib/pricing";
import { useCurrency } from "@/hooks/useCurrency";
import { CurrencySelector, CurrencyNote } from "@/components/CurrencySelector";
export default function DestinationsTable() {
  const {
    currency
  } = useCurrency();
  const getCurrencyLabel = (c: Currency): string => {
    switch (c) {
      case "EGP":
        return "Currency: Egyptian pound (EGP) · Tolls & Taxes Included";
      case "EUR":
        return "Currency: Euro (EUR), approximate conversion · Tolls & Taxes Included";
      case "USD":
        return "Currency: US dollar (USD), approximate conversion · Tolls & Taxes Included";
    }
  };
  return <section id="all-routes-fixed-prices" data-nav="light" className="relative bg-background text-foreground py-20 md:py-24 border-b border-border selection:bg-primary/20 selection:text-foreground">
      <div className="max-w-[1136px] mx-auto px-6">
        {/* Header Block */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="max-w-3xl">
          {/* Eyebrow with Signature Dot */}
          <div className="flex items-center gap-2 mb-3">
            <span className="size-2 rounded-full bg-primary" aria-hidden="true" />
            <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
              Fixed Per-Vehicle Rates
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-[2.6rem] font-bold tracking-tight text-foreground leading-[1.2]">
            All Routes & Fixed Prices
          </h1>

          {/* Signature Hairline Rule */}
          <div className="relative my-6 h-px w-full max-w-md bg-border" aria-hidden="true">
            <div className="absolute right-0 top-1/2 -translate-y-1/2 size-1.5 rounded-full bg-primary" />
          </div>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Every price is per vehicle, fixed in advance, and includes all highway tolls and Hurghada Airport parking fees. We monitor your flight's arrival time and any delays free of charge, with up to 60 minutes of free waiting time from the agreed meeting time. No surprises on arrival.
          </p>

          <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-medium text-foreground/85">
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="size-4 text-primary" aria-hidden="true" />
              <span>Direct door-to-door transfers</span>
            </div>
            <div className="flex items-center gap-1.5">
              <ShieldCheck className="size-4 text-primary" aria-hidden="true" />
              <span>Private door-to-door pickups</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Plane className="size-4 text-primary" aria-hidden="true" />
              <span>Free flight monitoring</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Clock className="size-4 text-primary" aria-hidden="true" />
              <span>60 min free waiting</span>
            </div>
          </div>
        </motion.div>

        {/* Currency Controls & Notes */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.05,
        ease: "easeOut"
      }} className="mt-8 flex flex-col md:flex-row md:items-start justify-between gap-6 p-5 rounded-md border border-border bg-card">
          <CurrencySelector locale="en" className="shrink-0" />
          <div className="flex flex-col gap-2 max-w-xl">
            <CurrencyNote locale="en" />
            <p className="text-xs text-muted-foreground border-t border-border/60 pt-2">
              We monitor your flight's arrival time and any delays free of charge. Your transfer includes up to 60 minutes of free waiting time from the agreed meeting time.
            </p>
          </div>
        </motion.div>

        {/* Ledger / Table Container */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.55,
        delay: 0.1,
        ease: "easeOut"
      }} className="mt-8 overflow-hidden rounded-md border border-border bg-card shadow-xs">
          {/* Table Header Description Bar */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-border bg-secondary/50 px-5 py-3.5 text-xs text-muted-foreground gap-2">
            <span>Departure: Hurghada International Airport (HRG)</span>
            <span>{getCurrencyLabel(currency)}</span>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-muted/40">
                <TableRow className="border-b border-border hover:bg-transparent">
                  <TableHead className="w-[45%] py-4 pl-6 text-xs font-semibold uppercase tracking-wider text-foreground">
                    Destination
                  </TableHead>
                  <TableHead className="w-[27.5%] py-4 px-4 text-xs font-semibold uppercase tracking-wider text-foreground">
                    <div className="flex items-center gap-2">
                      <Car className="size-4 text-primary" aria-hidden="true" />
                      <span>Standard Car — up to 4 passengers</span>
                    </div>
                  </TableHead>
                  <TableHead className="w-[27.5%] py-4 pr-6 text-xs font-semibold uppercase tracking-wider text-foreground">
                    <div className="flex items-center gap-2">
                      <Users className="size-4 text-primary" aria-hidden="true" />
                      <span>Minivan — up to 10 passengers</span>
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ROUTE_PRICES.map((route, i) => <TableRow key={route.id} id={`route-${route.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`} data-index={i} className="border-b border-border/70 hover:bg-accent/40 transition-[background-color] duration-150">
                    <TableCell className="py-4 pl-6 font-medium text-foreground">
                      <div className="flex items-center gap-2.5">
                        <span className="text-sm font-semibold text-foreground">
                          {route.name}
                        </span>
                        {route.popular && <span className="inline-flex items-center rounded-xs bg-[var(--chart-2)]/15 px-2 py-0.5 text-[11px] font-medium text-[var(--chart-2)] border border-[var(--chart-2)]/30">
                            Popular route
                          </span>}
                      </div>
                    </TableCell>
                    <TableCell className="py-4 px-4">
                      <div className="inline-flex items-baseline gap-1">
                        <span className="text-base font-semibold text-foreground">
                          {formatPrice(route.carEgp, currency, "en")}
                        </span>
                        <span className="text-xs text-muted-foreground">/ vehicle</span>
                      </div>
                    </TableCell>
                    <TableCell className="py-4 pr-6">
                      <div className="inline-flex items-baseline gap-1">
                        <span className="text-base font-semibold text-foreground">
                          {formatPrice(route.vanEgp, currency, "en")}
                        </span>
                        <span className="text-xs text-muted-foreground">/ vehicle</span>
                      </div>
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </div>

          {/* Group Bus note */}
          <div className="border-t border-border bg-secondary/30 px-5 py-3 text-xs text-muted-foreground">
            Bus: for groups of 10 or more — price on request.
          </div>
        </motion.div>

        {/* Bottom Booking Prompt & CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.2,
        ease: "easeOut"
      }} className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 p-6 rounded-md bg-secondary/60 border border-border">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              Ready to confirm your route?
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Send your route, travel dates, flight number, passenger count, and luggage details for review.
            </p>
          </div>
          <Button asChild size="lg" className="rounded-md shrink-0">
            <Link to="/BookYourPrivateTransferPage">
              <span>Book Your Private Transfer</span>
              <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>;
}